/**
 * AvailabilitySettings Component
 * Configure user availability for booking
 */

import { useState, useEffect } from 'react';
import { Clock, Save } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useBooking } from '@/hooks/useBooking';
import { useAuth } from '@/contexts/AuthContext';
import {
  BOOKING_TRANSLATIONS,
  AvailabilitySlot,
  DEFAULT_AVAILABILITY,
} from '@/types/booking.types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const DAYS_OF_WEEK = [
  { value: 0, labelKey: 'sunday' as const },
  { value: 1, labelKey: 'monday' as const },
  { value: 2, labelKey: 'tuesday' as const },
  { value: 3, labelKey: 'wednesday' as const },
  { value: 4, labelKey: 'thursday' as const },
  { value: 5, labelKey: 'friday' as const },
  { value: 6, labelKey: 'saturday' as const },
];

const TIME_OPTIONS = Array.from({ length: 24 }, (_, i) => {
  const hour = i.toString().padStart(2, '0');
  return [
    { value: `${hour}:00`, label: `${hour}:00` },
    { value: `${hour}:30`, label: `${hour}:30` },
  ];
}).flat();

const DURATION_OPTIONS = [15, 30, 45, 60, 90, 120];
const BUFFER_OPTIONS = [0, 5, 10, 15, 30];

export function AvailabilitySettings() {
  const { language } = useLanguage();
  const { user } = useAuth();
  const t = BOOKING_TRANSLATIONS[language === 'fr' ? 'fr' : 'en'];
  const { availability, saveAvailability, loading } = useBooking(user?.id);

  const [slots, setSlots] = useState<Record<number, { enabled: boolean; start: string; end: string }>>({});
  const [slotDuration, setSlotDuration] = useState(30);
  const [bufferTime, setBufferTime] = useState(15);

  useEffect(() => {
    if (availability) {
      const slotsMap: Record<number, { enabled: boolean; start: string; end: string }> = {};
      
      for (let i = 0; i < 7; i++) {
        const daySlot = availability.slots.find((s) => s.dayOfWeek === i);
        slotsMap[i] = daySlot
          ? { enabled: true, start: daySlot.startTime, end: daySlot.endTime }
          : { enabled: false, start: '09:00', end: '17:00' };
      }
      
      setSlots(slotsMap);
      setSlotDuration(availability.slotDuration);
      setBufferTime(availability.bufferBetweenMeetings);
    } else {
      // Default: Mon-Fri 9-17
      const defaultSlots: Record<number, { enabled: boolean; start: string; end: string }> = {};
      for (let i = 0; i < 7; i++) {
        const defaultDay = DEFAULT_AVAILABILITY.find((s) => s.dayOfWeek === i);
        defaultSlots[i] = defaultDay
          ? { enabled: true, start: defaultDay.startTime, end: defaultDay.endTime }
          : { enabled: false, start: '09:00', end: '17:00' };
      }
      setSlots(defaultSlots);
    }
  }, [availability]);

  const handleSave = async () => {
    const availabilitySlots: AvailabilitySlot[] = Object.entries(slots)
      .filter(([_, config]) => config.enabled)
      .map(([day, config]) => ({
        dayOfWeek: parseInt(day),
        startTime: config.start,
        endTime: config.end,
      }));

    await saveAvailability({
      slots: availabilitySlots,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      slotDuration,
      bufferBetweenMeetings: bufferTime,
    });
  };

  const updateSlot = (day: number, field: 'enabled' | 'start' | 'end', value: boolean | string) => {
    setSlots((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        [field]: value,
      },
    }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5" />
          {t.availability}
        </CardTitle>
        <CardDescription>{t.setAvailability}</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        {/* Duration & Buffer */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t.slotDuration}</Label>
            <Select
              value={slotDuration.toString()}
              onValueChange={(v) => setSlotDuration(parseInt(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DURATION_OPTIONS.map((d) => (
                  <SelectItem key={d} value={d.toString()}>
                    {d} {t.minutes}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>{t.bufferTime}</Label>
            <Select
              value={bufferTime.toString()}
              onValueChange={(v) => setBufferTime(parseInt(v))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {BUFFER_OPTIONS.map((b) => (
                  <SelectItem key={b} value={b.toString()}>
                    {b} {t.minutes}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Days */}
        <div className="space-y-3">
          {DAYS_OF_WEEK.map((day) => (
            <div
              key={day.value}
              className="flex items-center gap-4 p-3 rounded-lg border bg-card"
            >
              <Switch
                checked={slots[day.value]?.enabled ?? false}
                onCheckedChange={(checked) => updateSlot(day.value, 'enabled', checked)}
              />
              <span className="w-24 font-medium text-sm">{t[day.labelKey]}</span>

              {slots[day.value]?.enabled && (
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-xs text-muted-foreground">{t.from}</span>
                  <Select
                    value={slots[day.value]?.start || '09:00'}
                    onValueChange={(v) => updateSlot(day.value, 'start', v)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <span className="text-xs text-muted-foreground">{t.to}</span>
                  <Select
                    value={slots[day.value]?.end || '17:00'}
                    onValueChange={(v) => updateSlot(day.value, 'end', v)}
                  >
                    <SelectTrigger className="w-24">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ))}
        </div>

        <Button onClick={handleSave} disabled={loading} className="w-full">
          <Save className="w-4 h-4 mr-2" />
          {t.saveAvailability}
        </Button>
      </CardContent>
    </Card>
  );
}
